import React, { useState } from "react";
import { Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";

import DstAccordion from "../../../../components/DstAccordion/DstAccordion";
import DstButton from "../../../../components/DstButton/DstButton";
import InfoRow from "../../../../components/InfoRow/InfoRow";
import { PermissionComponent } from "../../../../services/PermissionGuard";
import { formatDateHumanly } from "../../../../services/TimeService";
import translate from "../../../../services/Translate";
import EditCursusStagePopup from "../EditCursusStagePopup/EditCursusStagePopup";
import { AboutCourseProps } from "./AboutCourse.d";

const AboutCourse = ({ course, language, onCourseUpdated }: AboutCourseProps) => {
  const [openEditPopup, setOpenEditPopup] = useState(false);

  if (!course) return null;

  const cursus = course.cursus || {};

  return (
    <div>
      <div className="d-flex align-items-center mb-2">
        <PermissionComponent neededPermission={[{ methods: ["PATCH"], route: "/cursus/stages/*" }]}>
          <DstButton
            btnClass="ms-auto"
            value={translate(language, "PAGES.COURSE.ABOUT_COURSE.EDIT")}
            btnImageBefore={<span className="dst-icon-edit-02 me-2" />}
            variant="light"
            clickFunction={() => setOpenEditPopup(true)}
          />
        </PermissionComponent>
      </div>
      <Row className="s-regular">
        <Col>
          <div className="s-bold mb-1 mt-3">
            {translate(language, "PAGES.COURSE.ABOUT_COURSE.OVERALL_INFORMATION.TITLE")}
          </div>
          {InfoRow(
            "dst-icon-archive",
            translate(language, "PAGES.COURSE.ABOUT_COURSE.OVERALL_INFORMATION.CURSUS_PAGE_VALIDITY_START"),
            formatDateHumanly(language, course.dt_start)
          )}
          {InfoRow(
            "dst-icon-archive",
            translate(language, "PAGES.COURSE.ABOUT_COURSE.OVERALL_INFORMATION.CURSUS_PAGE_VALIDITY_END"),
            formatDateHumanly(language, course.dt_end)
          )}
          {InfoRow(
            "dst-icon-archive",
            translate(language, "PAGES.COURSE.ABOUT_COURSE.OVERALL_INFORMATION.SHORT_NAME"),
            course.cursus_prefix
          )}
          {InfoRow(
            "dst-icon-archive",
            translate(language, "PAGES.COURSE.ABOUT_COURSE.OVERALL_INFORMATION.DEPLOYMENT_NAME"),
            course.deployment_name,
            <OverlayTrigger
              placement="right"
              overlay={
                <Tooltip id="deployment-name-tooltip">
                  {translate(language, "PAGES.COURSE.ABOUT_COURSE.OVERALL_INFORMATION.DEPLOYMENT_NAME_TOOLTIP")}
                </Tooltip>
              }
            >
              <span className="dst-icon-info-circle ms-1 text-muted clickable" />
            </OverlayTrigger>
          )}
          <div className="s-bold mb-1 mt-3">{translate(language, "PAGES.COURSE.ABOUT_COURSE.FORMAT.TITLE")}</div>
          {InfoRow("dst-icon-archive", translate(language, "PAGES.COURSE.ABOUT_COURSE.FORMAT.FORMAT"), course.format)}
          {InfoRow(
            "dst-icon-archive",
            translate(language, "PAGES.COURSE.ABOUT_COURSE.FORMAT.TOTAL_HOURS"),
            course.total_hours
          )}
          {InfoRow(
            "dst-icon-archive",
            translate(language, "PAGES.COURSE.ABOUT_COURSE.FORMAT.HOURS_SPENT_PROJECT"),
            course.hours_spent_on_project
          )}
          {InfoRow(
            "dst-icon-archive",
            translate(language, "PAGES.COURSE.ABOUT_COURSE.FORMAT.HOURS_SPENT_MODULES"),
            course.hours_spent_on_modules
          )}
          {InfoRow(
            "dst-icon-archive",
            translate(language, "PAGES.COURSE.ABOUT_COURSE.FORMAT.HOURS_SPENT_MASTERCLASS"),
            course.hours_spent_on_masterclass
          )}
          <div className="s-bold mb-1 mt-3">
            {translate(language, "PAGES.COURSE.ABOUT_COURSE.CONTRACTUALIZATION.TITLE")}
          </div>
          {InfoRow(
            "dst-icon-file-05",
            translate(language, "PAGES.COURSE.ABOUT_COURSE.CONTRACTUALIZATION.PRESENTATION_DOCUMENT"),
            course.presentation_document_url ? (
              <a
                href={course.presentation_document_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-decoration-none text-nowrap"
              >
                {translate(language, "PAGES.COURSE.ABOUT_COURSE.CONTRACTUALIZATION.VIEW_DOCUMENT")}
                <span className="dst-icon-link-external-01 ms-1" aria-hidden="true" />
              </a>
            ) : null
          )}
          <DstAccordion
            icon={<span className="dst-icon-file-attachment-02" />}
            title={translate(language, "PAGES.COURSE.ABOUT_COURSE.CONTRACTUALIZATION.END_COURSES_DOCUMENTS")}
            text={<div>{course.end_of_formation_document || "N/A"}</div>}
          />
          {InfoRow(
            "dst-icon-building-08",
            translate(language, "PAGES.COURSE.ABOUT_COURSE.CONTRACTUALIZATION.SCHOOL"),
            cursus.school?.name
          )}
          {course.pedagogy_leader && course.pedagogy_leader_references ? (
            <DstAccordion
              icon={<span className="dst-icon-user-square" />}
              title={translate(language, "PAGES.COURSE.ABOUT_COURSE.CONTRACTUALIZATION.PEDAGOGY_LEADER")}
              textRight={
                <Link to={`/user/${course.pedagogy_leader.id}`} className="text-decoration-none">
                  {course.pedagogy_leader.full_name}
                </Link>
              }
              text={<div className="text-pre-wrap">{course.pedagogy_leader_references}</div>}
            />
          ) : (
            InfoRow(
              "dst-icon-user-square",
              translate(language, "PAGES.COURSE.ABOUT_COURSE.CONTRACTUALIZATION.PEDAGOGY_LEADER"),
              course.pedagogy_leader ? `${course.pedagogy_leader.full_name}` : "N/A"
            )
          )}
          <DstAccordion
            icon={<span className="dst-icon-certificate-01" />}
            title={translate(language, "PAGES.COURSE.ABOUT_COURSE.CONTRACTUALIZATION.PEDAGOGY_LEADER_QUALOFOCATIONS")}
            text={<div className="text-pre-wrap">{course.pedagogy_leader_diplomas || "N/A"}</div>}
          />
          <DstAccordion
            icon={<span className="dst-icon-target-04" />}
            title={translate(language, "PAGES.COURSE.ABOUT_COURSE.CONTRACTUALIZATION.OBJECTIVES")}
            text={<div className="text-pre-wrap">{course.objectives || "N/A"}</div>}
          />
          <DstAccordion
            icon={<span className="dst-icon-building-05" />}
            title={translate(language, "PAGES.COURSE.ABOUT_COURSE.CONTRACTUALIZATION.MEDIATOR")}
            text={<div className="text-pre-wrap">{course.mediator || "N/A"}</div>}
          />
          <DstAccordion
            icon={<span className="dst-icon-list" />}
            title={translate(language, "PAGES.COURSE.ABOUT_COURSE.CONTRACTUALIZATION.CONTENT")}
            text={<div className="text-pre-wrap">{course.content || "N/A"}</div>}
          />
          <DstAccordion
            icon={<span className="dst-icon-file-check-02" />}
            title={translate(language, "PAGES.COURSE.ABOUT_COURSE.CONTRACTUALIZATION.EVALUATION")}
            text={<div className="text-pre-wrap">{course.evaluations || "N/A"}</div>}
          />
          <DstAccordion
            icon={<span className="dst-icon-bezier-curve-03" />}
            title={translate(language, "PAGES.COURSE.ABOUT_COURSE.CONTRACTUALIZATION.TRAINING_ORGANIZATION")}
            text={<div className="text-pre-wrap">{course.training_organization || "N/A"}</div>}
          />
          <DstAccordion
            icon={<span className="dst-icon-check-circle-broken" />}
            title={translate(language, "PAGES.COURSE.ABOUT_COURSE.CONTRACTUALIZATION.PREREQUISITES")}
            text={<div className="text-pre-wrap">{course.prerequisites || "N/A"}</div>}
          />
        </Col>
        <Col>
          <div className="border border-primary-subtle rounded p-3 mt-3">
            <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
              <div className="s-bold d-flex align-items-center">
                <span className="dst-icon-info-circle me-2" aria-hidden="true" />
                {translate(language, "PAGES.COURSE.ABOUT_COURSE.CURSUS.TITLE")}
              </div>
            </div>
            <p className="text-muted xxs-regular mb-3">
              {translate(language, "PAGES.COURSE.ABOUT_COURSE.CURSUS.HELP_TEXT")}
            </p>
            {InfoRow(
              "dst-icon-archive",
              translate(language, "PAGES.COURSE.ABOUT_COURSE.CURSUS.FULL_NAME"),
              cursus.name
            )}
            <DstAccordion
              icon={<span className="dst-icon-file-attachment-02" />}
              title={translate(language, "PAGES.COURSE.ABOUT_COURSE.CURSUS.DESCRIPTION")}
              text={<div className="text-pre-wrap">{cursus.description || "N/A"}</div>}
            />
            {InfoRow(
              "dst-icon-archive",
              translate(language, "PAGES.COURSE.ABOUT_COURSE.CURSUS.PRODUCT_LEADER"),
              cursus.owner ? (
                <Link
                  to={`/user/${cursus.owner.id}`}
                  className="text-decoration-none"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {cursus.owner.first_name} {cursus.owner.last_name}
                </Link>
              ) : (
                "N/A"
              )
            )}
          </div>
          <div className="s-bold mb-1 mt-3">{translate(language, "PAGES.COURSE.ABOUT_COURSE.CERTIFICATES.TITLE")}</div>
          <div className="s-bold mb-1 mt-3">RNCP</div>
          {InfoRow(
            "dst-icon-clipboard",
            translate(language, "PAGES.COURSE.ABOUT_COURSE.RNCP.ENTITLED"),
            course.external_compliance?.name
          )}
          {InfoRow(
            "dst-icon-clipboard",
            translate(language, "PAGES.COURSE.ABOUT_COURSE.RNCP.CODE"),
            course.external_compliance?.code
          )}
          {InfoRow(
            "dst-icon-clipboard",
            translate(language, "PAGES.COURSE.ABOUT_COURSE.RNCP.NAME_CERTIFIER"),
            course.external_compliance?.certifier_name
          )}
          {InfoRow(
            "dst-icon-clipboard",
            translate(language, "PAGES.COURSE.ABOUT_COURSE.RNCP.FORMAT"),
            course.external_compliance?.type
          )}
          <DstAccordion
            icon={<span className="dst-icon-target-04" />}
            title={translate(language, "PAGES.COURSE.ABOUT_COURSE.RNCP.SKILL_BLOCK")}
            text={
              <React.Fragment>
                {course.external_compliance?.skill_blocks.length
                  ? course.external_compliance.skill_blocks.map((block, index) => (
                      <div key={block.id || index} className="mb-2">
                        <strong>
                          {index + 1}. {block.name}
                        </strong>
                        <div className="text-pre-line">{block.content}</div>
                      </div>
                    ))
                  : "N/A"}
              </React.Fragment>
            }
          />
          {InfoRow(
            "dst-icon-clipboard",
            translate(language, "PAGES.COURSE.ABOUT_COURSE.RNCP.INTERNSHIP_NEEDED"),
            course.external_compliance?.internship_needed
              ? translate(language, "PAGES.COURSE.ABOUT_COURSE.YES")
              : translate(language, "PAGES.COURSE.ABOUT_COURSE.NO")
          )}
          {InfoRow(
            "dst-icon-clipboard",
            translate(language, "PAGES.COURSE.ABOUT_COURSE.RNCP.INTERNSHIP_LENGTH_HOURS"),
            course.external_compliance?.internship_duration_hours
          )}
          {InfoRow(
            "dst-icon-clipboard",
            translate(language, "PAGES.COURSE.ABOUT_COURSE.RNCP.INTERNSHIP_LENGTH_DAYS"),
            course.external_compliance?.internship_duration_days
          )}
          <DstAccordion
            icon={<span className="dst-icon-clipboard" />}
            title={translate(language, "PAGES.COURSE.ABOUT_COURSE.RNCP.INTERNSHIP_EXEMPTION_CRITERIA")}
            text={
              <div className="text-pre-wrap">
                {course.external_compliance?.internship_exemption_conditions || "N/A"}
              </div>
            }
          />
          {InfoRow(
            "dst-icon-clipboard",
            translate(language, "PAGES.COURSE.ABOUT_COURSE.RNCP.DEFENSE"),
            course.external_compliance?.end_of_study_defense
              ? translate(language, "PAGES.COURSE.ABOUT_COURSE.YES")
              : translate(language, "PAGES.COURSE.ABOUT_COURSE.NO")
          )}
          {InfoRow(
            "dst-icon-file-05",
            translate(language, "PAGES.COURSE.ABOUT_COURSE.RNCP.BLANK_CERTIFICATE_TEMPLATE"),
            course.external_compliance?.useful_links ? (
              <div className="d-flex flex-column">
                {JSON.parse(course.external_compliance.useful_links).map((link: string, index: number) =>
                  link.startsWith("http") ? (
                    <a key={index} href={link} target="_blank" rel="noopener noreferrer">
                      {link}
                    </a>
                  ) : (
                    <span key={index}>{link}</span>
                  )
                )}
              </div>
            ) : (
              "N/A"
            )
          )}
        </Col>
      </Row>
      <EditCursusStagePopup
        course={course}
        language={language}
        openPopup={openEditPopup}
        setOpenPopup={setOpenEditPopup}
        onCourseUpdated={onCourseUpdated}
      />
    </div>
  );
};

export default AboutCourse;
